import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Box, Button, Card, CardActions, CardContent, Grid, Stack, TextField, Typography} from '@mui/material';
import {GlobalStateContext} from '../../state/GlobalStateProvider';
import {useAppointmentGraphQL} from '../../service/useAppointmentGraphQL';
import {useAppointmentsAPI} from '../../service/useAppointmentsAPI';
import {useTranslation} from 'react-i18next';
import {formatDate, formatIsoToHHMM, toLocalDate} from '../../common/DateUtil';
import {gql, useQuery} from "@apollo/client";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import nl from "date-fns/locale/nl";
import ServiceCard from "../../components/common/ServiceCard";

const GET_CONFIGURATION_PARAMETER = gql`
    query ConfiguratieParameterByType($type: ConfigurationParameterType!) {
        configuratieParameterByType(type: $type) {
            id
            startDate
            endDate
            amount
            type
        }
    }
`;

export default function AppointmentTimeslotStep({updateCurrentStep}) {
    const {globalState, updateGlobalState, setPartOfGlobalState} = useContext(GlobalStateContext);
    const {data} = useQuery(GET_CONFIGURATION_PARAMETER, {variables: {type: 'BOOKING_OPEN_DATE'}});

    // reservation configured start and end date
    const configuredStart = data?.configuratieParameterByType?.[0]?.startDate ? new Date(data.configuratieParameterByType[0].startDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startCandidate = configuredStart ? new Date(configuredStart) : today;
    startCandidate.setHours(0, 0, 0, 0);
    const reservationStart = startCandidate < today ? today : startCandidate;
    const reservationEnd = new Date(data?.configuratieParameterByType?.[0]?.endDate);

    const {useActiveServices} = useAppointmentGraphQL();
    const {data: servicesData} = useActiveServices();
    const {getOpenAppointmentDatesForServiceAndDate} = useAppointmentsAPI();
    const {t} = useTranslation();

    const [date, setDate] = useState();
    const [openAppointmentSchedules, setOpenAppointmentSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const selectedService = useMemo(() => (servicesData?.services || []).find(s => s.uuid === globalState.appointment.selectedServiceUuid) || null, [servicesData, globalState.appointment.selectedServiceUuid]);

    useEffect(() => {
        if (!date) return;
        setLoading(true);
        Promise.all([
            getOpenAppointmentDatesForServiceAndDate(selectedService.uuid, toLocalDate(date)).then(setOpenAppointmentSchedules),
        ])
            .catch(() => setError(t('timeslotsFetchFailed')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const canProceed = globalState.appointment.selectedAppointments !== null && globalState.appointment.selectedAppointments.length > 0;
    const goBack = () => updateCurrentStep(2);
    const onNext = async () => updateCurrentStep(4);

    useEffect(() => {
        updateGlobalState({appointment: {selectedStartAt: null, computedEndAt: null}});
        // Clear any previously selected appointments when date or service changes
        setPartOfGlobalState('appointment.selectedAppointments', []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, selectedService?.uuid]);

    const toggleAppointment = (openAppointment, animalKey, animal) => {
        const {appointment} = globalState;

        // Is this exact timeslot already selected for this row?
        const isSelectedForThisRow = appointment.selectedAppointments.find(
            value => value.uuid === openAppointment.uuid && value._animalKey === animalKey
        );

        if (isSelectedForThisRow) {
            // Deselect it for this row
            const remaining = appointment.selectedAppointments.filter(
                value => !(value.uuid === openAppointment.uuid && value._animalKey === animalKey)
            );
            setPartOfGlobalState('appointment.selectedAppointments', remaining);
            return;
        }

        // Replace any existing selection for this row with the new one
        const withoutThisRow = appointment.selectedAppointments.filter(value => value._animalKey !== animalKey);
        const withNew = [...withoutThisRow, {...openAppointment, _animalKey: animalKey, animalId: animal.id, animalName: animal.animalName}];
        setPartOfGlobalState('appointment.selectedAppointments', withNew);
    };

    const animalNameAndIds = () => {
        const ids = globalState.reservationMeta?.animalsInReservationIds || [];
        const names = globalState.reservationMeta?.animalsInReservationNames || [];
        const len = Math.max(ids.length, names.length);
        return Array.from({length: len}, (_, index) => ({
            index,
            animalName: names[index],
            id: ids[index]
        })).filter(item => item.animalName !== undefined || item.id !== undefined);
    }

    return (
        <Grid container alignItems="flex-start" justifyContent="center" spacing={2} sx={{textAlign: 'left'}}>
            <Grid item xs={12} sm={10} md={8} lg={6}>
                <Card variant="outlined">
                    <CardContent>
                        {selectedService && (
                            <Box sx={{display: 'flex', mb: 2}}>
                                <Stack direction="column" spacing={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>{t('chooseTimeslot')}</Typography>
                                    <Typography variant="subtitle1">{selectedService.name} - {selectedService.durationMinutes} {t('minutes')} •
                                        €{selectedService.price}</Typography>
                                </Stack>
                            </Box>
                        )}
                        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                                <DatePicker
                                    label="Check-in datum"
                                    value={date}
                                    minDate={reservationStart}
                                    maxDate={reservationEnd}
                                    onChange={(newValue) => setDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required={false}
                                            // keep DatePicker's own validation signal if you want:
                                            error={params.error}
                                            helperText={params.helperText}
                                            inputProps={{ ...params.inputProps, "data-testid": "appointment-date-input" }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Box>

                        {loading && <Typography>{t('loadingTimeslots')}</Typography>}
                        {!loading && openAppointmentSchedules.length === 0 && date !== undefined && (
                            <Typography color="warning.main" data-testid="no-timeslots-alert">{t('noTimeslotsSuggestion')}</Typography>
                        )}
                        {!loading && openAppointmentSchedules.length > 0 && (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {animalNameAndIds().map((animal) => (
                                    <Box key={`row-${animal.id ?? animal.index}`} sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                        {/** Animal label (if available) */}
                                        {animal.animalName && (
                                            <Typography variant="subtitle2" sx={{mr: 1}}>
                                                {animal.animalName}
                                            </Typography>
                                        )}
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                            {openAppointmentSchedules.map((openAppointment) => {
                                                const animalKey = (animal.id ?? `idx-${animal.index}`);
                                                const selections = globalState?.appointment?.selectedAppointments || [];
                                                const isSelectedForRow = selections.some(
                                                    value => value.uuid === openAppointment.uuid && value._animalKey === animalKey
                                                );
                                                const isTakenByOtherRow = selections.some(
                                                    value => value.uuid === openAppointment.uuid && value._animalKey !== animalKey
                                                );
                                                return (
                                                    <Button
                                                        key={`${animal.id ?? animal.index}-${openAppointment.startAt}`}
                                                        variant={isSelectedForRow ? 'contained' : 'outlined'}
                                                        disabled={isTakenByOtherRow}
                                                        onClick={() => toggleAppointment(openAppointment, animalKey, animal)}
                                                        data-testid={`timeslot-button-${formatIsoToHHMM(openAppointment.startAt)}-${animal.animalName.replace(/\s+/g, '-').toLowerCase()}`}
                                                    >
                                                        {formatIsoToHHMM(openAppointment.startAt)}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {/*{inlineSummary && (*/}
                        {/*    <Box sx={{mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1}}>*/}
                        {/*        <Typography variant="body2">{inlineSummary}</Typography>*/}
                        {/*    </Box>*/}
                        {/*)}*/}

                        {error && (
                            <Typography sx={{mt: 1}} color="error" data-testid="timeslots-error">{error}</Typography>
                        )}

                    </CardContent>
                    <CardActions style={{marginBottom: '10px', justifyContent: 'space-between'}}>

                        <Button variant="outlined" onClick={goBack} data-testid="previous-button">{t('previous')}</Button>
                        <Button variant="contained" onClick={onNext} disabled={!canProceed} data-testid="next-button">{t('next')}</Button>
                    </CardActions>
                </Card>
            </Grid>
        </Grid>
    );
}
